import styled from "@emotion/styled";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import ParkkiButton from "components/Button";
import { SwitchInput } from "components/HookFormComponents/SwitchInput";
import React, { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import DisplaysUtils from "services/displays/utils";
import MapActioner from "services/map/actioner";
import ParkkiTheme from "themes/ParkkiTheme";
import DisplayMirrorAlert from "./DisplayMirrorAlert";
import DisplayRuleForm from "./DisplayRuleForm";

const StyledText = styled.span`
  color: ${ParkkiTheme.colors.turquoise};
`;

function DisplayFrom({
  display,
  displays,
  retrieveMapData,
  displayInitialized,
}) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const currentDisplay = useMemo(
    () => displays?.find((d) => d?.id === display?.id),
    [displays, display]
  );
  const defaultValues = useMemo(
    () => DisplaysUtils.getFormDefaultValues(currentDisplay, displays),
    [currentDisplay, displays]
  );
  const methods = useForm({
    reValidateMode: "onChange",
    mode: "all",
    defaultValues,
  });

  const { getFieldState } = methods;

  const emergencyRule = getFieldState("emergency_rule");
  const standBy = getFieldState("stand_by");
  const rule = getFieldState("rule");

  const currentDisplayId = methods.watch("id");

  useEffect(() => {
    if (displayInitialized && !methods.formState.isDirty && !open) {
      methods.reset(defaultValues);
    }
  }, [defaultValues, displayInitialized, methods, open]);

  useEffect(() => {
    if (currentDisplayId !== currentDisplay.id) {
      methods.reset(defaultValues);
    }
  }, [currentDisplay, methods, defaultValues, currentDisplayId]);

  const handleSubmit = () => {
    setOpen(true);
  };

  const resetAfterUpdate = () => {
    methods.reset(methods.getValues());
  };

  const udpate = () => {
    const fields = methods.getValues();
    let promises = [];
    if (standBy.isDirty) {
      promises.push(
        DisplaysUtils.updateStandBy(
          currentDisplayId,
          currentDisplay,
          fields?.stand_by
        )
      );
    }
    if (rule.isDirty) {
      promises.push(
        DisplaysUtils.updateRule(currentDisplayId, currentDisplay, fields?.rule)
      );
    }
    if (emergencyRule.isDirty) {
      promises.push(
        DisplaysUtils.updateEmergencyRule(
          currentDisplayId,
          fields?.emergency_rule
        )
      );
    }

    return Promise.all(promises).then(() => {
      toast(intl.messages["toast.success.updateDisplaySuccess"], {
        position: toast.POSITION.BOTTOM_RIGHT,
        type: toast.TYPE.SUCCESS,
      });
      setOpen(false);
      setTimeout(retrieveMapData, 5000);
      setTimeout(resetAfterUpdate, 1000);
    });
  };

  const confirmationDialogContent = useMemo(() => {
    if (standBy.isDirty) {
      if (methods.getValues()?.stand_by) {
        return intl.formatMessage({
          id: "displaysScene.dialog.standbyForced",
        });
      } else {
        return intl.formatMessage({
          id: "displaysScene.dialog.standbyRemoved",
        });
      }
    }
    if (rule.isDirty) {
      if (methods.getValues()?.rule?.is_active) {
        return intl.formatMessage(
          {
            id: "displaysScene.dialog.ruleForced",
          },
          {
            text: DisplaysUtils.isCustom(methods.getValues()?.rule?.text)
              ? methods.getValues()?.rule?.custom_text
              : methods.getValues()?.rule?.text,
            color: intl.formatMessage({
              id: `displaysScene.colors.${
                currentDisplay?.available_colors?.find(
                  (c) => c.symbol === methods.getValues()?.rule?.color
                )?.name
              }`,
            }),
          }
        );
      } else {
        return intl.formatMessage({
          id: "displaysScene.dialog.ruleRemoved",
        });
      }
    }

    if (emergencyRule.isDirty) {
      return intl.formatMessage({
        id: "displaysScene.dialog.emergencyRuleRemoved",
      });
    }
  }, [
    currentDisplay?.available_colors,
    emergencyRule,
    intl,
    methods,
    rule.isDirty,
    standBy.isDirty,
  ]);

  return (
    <>
      <FormProvider {...methods}>
        <Box
          component="form"
          sx={{ display: "flex", flexDirection: "column", width: "100%" }}
          onSubmit={methods.handleSubmit(handleSubmit)}
          noValidate
        >
          <SwitchInput
            name="stand_by"
            disabled={display.is_awol}
            label={intl.formatMessage({ id: "displaysScene.standbyForced" })}
          />
          {currentDisplay.emergency_rule?.rule !== undefined && (
            <SwitchInput
              name="emergency_rule"
              disabled={
                display.is_awol ||
                currentDisplay?.emergency_rule?.rule?.is_active === false
              }
              label={intl.formatMessage({
                id: "displaysScene.emergencyRuleForced",
              })}
              tooltipContent={
                currentDisplay?.emergency_rule?.rule?.is_active === false
                  ? intl.formatMessage({
                      id: "displaysScene.emergencyRuleTooltip",
                    })
                  : undefined
              }
            />
          )}
          <DisplayRuleForm display={display} />
          <DisplayMirrorAlert display={display} />
          <Button
            sx={{ mt: 3 }}
            variant="contained"
            type="submit"
            disabled={!methods.formState.isDirty}
          >
            <FormattedMessage id="displaysScene.save" />
          </Button>
        </Box>
      </FormProvider>
      <Dialog open={open} fullWidth>
        <DialogTitle>
          <FormattedMessage id="displaysScene.dialog.title" />
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center" }}>
          <>
            <StyledText>{confirmationDialogContent}</StyledText>
            <StyledText>
              {DisplaysUtils.hasPictogram(display) &&
                methods.getValues()?.rule?.is_active &&
                !methods.getValues()?.stand_by && (
                  <>
                    <br />
                    <FormattedMessage
                      id="displaysScene.dialog.pictogramRuleForced"
                      values={{
                        text: intl.formatMessage({
                          id: `displaysScene.pictoColors.${
                            DisplaysUtils.getPictogram(
                              display,
                              displays
                            )?.available_colors?.find(
                              (c) =>
                                c.symbol ===
                                methods.getValues()?.rule?.pictogram
                            )?.name
                          }`,
                        }),
                      }}
                    />
                  </>
                )}
            </StyledText>
          </>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>
            <FormattedMessage id="configSectorsScene.dialog.cancel" />
          </Button>
          <ParkkiButton
            onClick={() => udpate()}
            fullWidth={false}
            label={<FormattedMessage id="configSectorsScene.dialog.confirm" />}
          />
        </DialogActions>
      </Dialog>
    </>
  );
}

const mapStateToProps = (state) => ({
  displays: state.Displays.list.items,
  displayInitialized: state.Displays.list.initialized,
  display: DisplaysUtils.getSelectedLayer(state.Displays),
});

const mapDispatchToProps = (dispatch) => ({
  retrieveMapData: () => dispatch(MapActioner.retrieveMapDataForDisplaysTab()),
});

export default connect(mapStateToProps, mapDispatchToProps)(DisplayFrom);
